// selectors/displayModel.js
import { createSelector } from 'reselect';
import { getBandsPosition } from '../../components/Utils/LayoutUtils';

/**
 * This module computes:
 *  - calculatedDisplayMode: finalized display mode per level ('stack' | 'card' | ...)
 *  - fusedDataColumns: per-level columns after fusing inside each *geometric* stack block
 *
 * Key ideas:
 *  - A "stack block" is a consecutive region of levels marked 'stack' that also stay
 *    geometrically overlapped. If a level in 'stack' starts far enough to the right
 *    (outOfStack) relative to the current block boundary, it begins a NEW stack block.
 *  - Columns are fused (deduplicated) within each stack block independently.
 *  - We iterate because fused widths affect geometry, which can in turn affect block cuts.
 */

// -------------------- tiny helpers & constants --------------------

const STACK_WIDTH_THRESHOLD = 300;

/** Shallow equality for arrays of primitive values. */
const arraysEqual = (a, b) =>
  a === b || (a && b && a.length === b.length && a.every((v, i) => v === b[i]));

/** Sum of header widths for an array of column descriptors. */
const headerWidth = (cols) =>
  Array.isArray(cols) ? cols.reduce((acc, c) => acc + (c?.w ?? 0), 0) : 0;

// -------------------- single-pass display-mode update --------------------

/**
 * PANELS WITHOUT ENOUGH ( < STACK_WIDTH_THRESHOLD ) SPACE ARE STACKED
 * PANELS AFTER STACKED ARE STACKED IF INSIDE "BLOCK ZONE"
 *
 * Compute one iteration of the display mode array, given:
 *  - rawDM: the raw/requested displayMode per level ('stack' | 'card' | ...)
 *  - panels: layout info per level (used by getBandsPosition and width thresholding)
 *  - fused: fused data columns per level (affects horizontal footprint)
 *
 * Rules (single pass, left→right):
 *  1) If a panel is narrow (< STACK_WIDTH_THRESHOLD), force this level to 'stack'.
 *  2) Otherwise, allow unstacking only if the band's starting x-position
 *     is strictly greater than the minimal boundary built from previous levels:
 *        minForOut = max over i<index of ( headerWidth_fused[i] + bandStart[i] )
 *     If we haven't crossed that boundary and the previous level was 'stack',
 *     keep stacking (unless the raw mode explicitly says 'card', which we honor).
 *
 * This function does NOT mutate inputs; it returns a new array.
 *
 * @param {Array<'stack'|'card'|string>} rawDM
 * @param {Array<object>} panels
 * @param {Array<Array<object>>} fused
 * @returns {Array<'stack'|'card'|string>}
 */
const computeDisplayModeOnce = (rawDM, panels, fused) =>
  rawDM.reduce((acc, elem, index) => {
    // Base decision: small panels force 'stack'; else start from the raw mode.
    let res = panels[index]?.width < STACK_WIDTH_THRESHOLD ? 'stack' : elem;
    const prev = acc[index - 1];

    // ---- Compute the minimal boundary required to "exit" a stack ----
    // minForOut accumulates the largest (header width + band start) among all previous levels.
    let minForOut = 0;
    for (let i = 0; i < index; i++) {
      let w = headerWidth(fused?.[i] ?? []);
      w += getBandsPosition(i, panels).start; // assumes { start } in px
      if (w > minForOut) minForOut = w;
    }

    let hasStackBefore = false;
    for (let i = 0; i < index; i++) {
      hasStackBefore =
        hasStackBefore || acc?.[i] === 'stack' || acc?.[i] === 'stackcard';
    }

    // Current band's starting x-position
    const { start } = getBandsPosition(index, panels);
    const outOfStack = start > minForOut + 1000000;

    // If we're not stacked but haven't crossed the boundary yet AND the previous level was stacked,
    // continue stacking—except if the raw mode explicitly requests 'card', which we preserve.
    if (res !== 'stack' && hasStackBefore) {
      // res = prev === 'stack' ? (elem === 'card' ? 'card' : 'stack') : res;
      res = hasStackBefore ? (elem === 'card' ? 'stackcard' : 'stack') : res;
    }

    acc.push(res);
    return acc;
  }, []);

// -------------------- multi-block detection & fuse --------------------

/**
 * PANEL ARE GROUPED IN BLOCKS
 * Compute a "stack block id" per level:
 *  - -1 for non-stack levels
 *  - 0, 1, 2, ... for successive stack blocks
 *
 * A new block starts if:
 *  - we transition from non-stack -> stack, OR
 *  - we are in stack but the current band's start is beyond the current block boundary (outOfStack)
 *
 * The block boundary is maintained as: max(start + fusedHeaderWidth) for levels inside the block.
 *
 * @param {Array<'stack'|'card'|string>} displayMode
 * @param {Array<object>} panels
 * @param {Array<Array<object>>} fused
 * @returns {Array<number>} blocks (same length as displayMode)
 */
// small safety margin to avoid jitter from tiny pixel diffs
const STACK_BLOCK_GAP = 6;

const computeStackBlocks = (displayMode, panels, fused) => {
  const blocks = new Array(displayMode.length).fill(-1);

  let currentBlock = -1;
  let blockStart = 0; // absolute x of current block origin
  let blockWidth = 0; // fused header footprint accumulated in this block

  for (let index = 0; index < displayMode.length; index++) {
    let hasStackBefore = false;
    for (let i = 0; i < index; i++) {
      hasStackBefore =
        hasStackBefore ||
        displayMode?.[i] === 'stack' ||
        displayMode?.[i] === 'stackcard';
    }
    if (displayMode[index] !== 'stack' && !hasStackBefore) {
      blocks[index] = -1;
      continue;
    }

    const { start } = getBandsPosition(index, panels);
    const width = headerWidth(fused?.[index] ?? []);

    if (currentBlock === -1) {
      // first stacked level → start block
      currentBlock = 0;
      blockStart = start;
      blockWidth = 0;
    } else {
      // detach if this level begins beyond the current block footprint
      const threshold = blockStart + blockWidth + STACK_BLOCK_GAP;
      if (start > threshold + 100000000) {
        currentBlock += 1;
        blockStart = start; // new origin
        blockWidth = 0; // reset footprint for the new block
      }
    }

    blocks[index] = currentBlock;

    // update block footprint: max distance from origin plus fused width
    const footprint = start - blockStart + width;
    if (footprint > blockWidth) blockWidth = footprint;
  }

  return blocks;
};

/**
 * STACKED NODES DISPLAY SHARED DATA-COLUMNS
 * Fuse columns inside each stack block.
 *  - Each block (id >= 0) receives a single deduplicated column set,
 *    assigned to every level of that block.
 *  - Non-stack levels (block = -1) keep their own columns.
 *
 * Deduplication key: col.data || col.key || JSON.stringify(col)
 *
 * @param {Array<Array<object>>} dataCols
 * @param {Array<number>} blocks
 * @returns {Array<Array<object>>}
 */
const fuseDataColumnsByBlocks = (dataCols, blocks) => {
  const out = dataCols.map((cols) => (cols ? [...cols] : []));
  const maxBlock = Math.max(-1, ...blocks);

  for (let b = 0; b <= maxBlock; b++) {
    const indices = [];
    for (let i = 0; i < blocks.length; i++)
      if (blocks[i] === b) indices.push(i);
    if (indices.length === 0) continue;

    const seen = new Set();
    const fused = [];

    for (const k of indices) {
      for (const col of dataCols?.[k] ?? []) {
        const key =
          (col && (col.data ?? col.key)) ?? JSON.stringify(col ?? null);
        if (!seen.has(key)) {
          seen.add(key);
          fused.push(col);
        }
      }
    }

    for (const k of indices) out[k] = fused;
  }

  return out;
};

// -------------------- inputs --------------------

const selectRawDM = (s) => s.objects?.displayMode ?? [];
const selectPanels = (s) => s.objects?.panels ?? [];
const selectDataCols = (s) => s.objects?.dataColumns ?? [];

// -------------------- root selector (bounded convergence) --------------------

/**
 * We iterate because:
 *  - fused widths => geometry => may change block cutting & displayMode decisions
 *  - displayMode (stack/non-stack) => blocks => changes which columns are fused
 *
 * Convergence criterion:
 *  - We stop when both displayMode and block partition stop changing,
 *    or when MAX iterations are reached (safety cap).
 */
export const selectDisplayModel = createSelector(
  [selectRawDM, selectPanels, selectDataCols],
  (rawDM, panels, dataCols) => {
    let dm = rawDM.slice();
    let fused = dataCols.map((c) => (c ? [...c] : []));
    let prevBlocks = new Array(dm.length).fill(-2); // impossible id to force first iteration
    const MAX = Math.max(7, dm.length); // safe upper bound; at least number of levels

    for (let step = 0; step < MAX; step++) {
      // 1) Compute geometric stack blocks given current fused widths
      const blocks = computeStackBlocks(dm, panels, fused);

      // 2) Fuse columns per block
      fused = fuseDataColumnsByBlocks(dataCols, blocks);

      // 3) Recompute display mode once using current fused geometry
      const nextDM = computeDisplayModeOnce(rawDM, panels, fused);

      // 4) Check convergence: displayMode and block partition stable?
      const dmStable = arraysEqual(nextDM, dm);
      const blocksStable = arraysEqual(blocks, prevBlocks);

      dm = nextDM;
      prevBlocks = blocks;

      if (dmStable && blocksStable) break;
    }
    console.log('calcDispMode', dm);
    console.log('fusedDataColumns', fused);
    return { calculatedDisplayMode: dm, fusedDataColumns: fused };
  }
);

// -------------------- convenience derived selectors --------------------

export const selectCalculatedDisplayMode = createSelector(
  [selectDisplayModel],
  (m) => m.calculatedDisplayMode
);

export const selectFusedDataColumns = createSelector(
  [selectDisplayModel],
  (m) => m.fusedDataColumns
);

/**
 * For each level L, returns:
 *   fusedLevelWidth[L] = max(0, headerWidth(fused[L]) - (start(L) - blockStart(blockOfL)))
 * Non-stack levels get 0.
 *
 * Uses the current calculatedDisplayMode/fusedDataColumns and the same
 * block geometry (computeStackBlocks).
 */
export const selectFusedLevelWidth = createSelector(
  [selectPanels, selectDisplayModel],
  (panels, model) => {
    const { calculatedDisplayMode: dm, fusedDataColumns: fused } = model;

    // 1) Partition levels into geometric stack blocks
    const blocks = computeStackBlocks(dm, panels, fused);

    // 2) Find the first level (origin) of each block to get blockStart
    const blockStartById = new Map();
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      if (b >= 0 && !blockStartById.has(b)) {
        blockStartById.set(b, getBandsPosition(i, panels).start);
      }
    }

    // 3) Compute per-level remaining width relative to its block start
    const fusedLevelWidth = dm.map((mode, level) => {
      const blockId = blocks[level];
      if (blockId < 0) return 0;

      const blockStart = blockStartById.get(blockId) ?? 0;
      const start = getBandsPosition(level, panels).start;
      const rel = Math.max(0, start - blockStart);
      const width = headerWidth(fused?.[level] ?? []);

      return Math.max(0, width - rel);
    });

    return fusedLevelWidth;
  }
);
